import {
  NavItem,
  Spinner
} from "@patternfly/react-core";
import {
  PropsWithChildren,
  MouseEvent as ReactMouseEvent,
  Suspense,
  useMemo
} from "react";
import { useTranslation } from "react-i18next";
import {
  To,
  matchPath,
  useHref,
  useLinkClickHandler,
  useLocation
} from "react-router-dom";
import type { Feature } from "../environment";
import { TFuncKey } from "../i18n";
import { useEnvironment } from "./KeycloakContext";
import {
  faBoxesStacked,
  faCubesStacked,
  faLaptop,
  faList,
  faUser,
  faUserGroup,
  faUserShield,
  faUsersLine, IconDefinition
} from "@fortawesome/free-solid-svg-icons";
//@ts-ignore
import { Menu, MenuItem } from "@l8/storybook";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type RootMenuItem = {
  label: TFuncKey;
  path: string;
  isVisible?: keyof Feature;
  modulePath?: string;
  icon: IconDefinition;
  subItem?: boolean;
};

type MenuItemWithChildren = {
  label: TFuncKey;
  children: MenuItem[];
  isVisible?: keyof Feature;
  icon: IconDefinition;
};


export type MenuItem = RootMenuItem | MenuItemWithChildren;

const menuItems: MenuItem[] = [
  { "label": "personalInfo", "path": "/", "icon": faUser },
  {
    "label": "accountSecurity",
    "icon": faUserShield,
    "children": [
      { "label": "signingIn", "path": "account-security/signing-in", "icon": faList, subItem: true },
      { "label": "deviceActivity", "path": "account-security/device-activity", "icon": faLaptop, subItem: true },
      {
        "label": "linkedAccounts",
        "path": "account-security/linked-accounts",
        "isVisible": "isLinkedAccountsEnabled",
        "icon": faUsersLine,
        subItem: true
      }
    ]
  },
  { "label": "applications", "path": "applications", "icon": faBoxesStacked },
  {
    "label": "groups",
    "path": "groups",
    "isVisible": "isViewGroupsEnabled",
    "icon": faUserGroup
  },
  {
    "label": "resources",
    "path": "resources",
    "isVisible": "isMyResourcesEnabled",
    "icon": faCubesStacked
  }
];

export const PageNav = () => {

  const context = useEnvironment();


  return (
    <Menu>
      {menuItems
        ?.filter((menuItem) =>
          menuItem.isVisible
            ? context.environment.features[menuItem.isVisible as keyof Feature]
            : true
        )
        .map((menuItem) => (
          <NavMenuItem
            key={menuItem.label as string}
            menuItem={menuItem}
          />
        ))}
    </Menu>
  );
};

type NavMenuItemProps = {
  menuItem: MenuItem;
};

function NavMenuItem({ menuItem }: NavMenuItemProps) {
  const { t } = useTranslation();
  const {
    environment: { features }
  } = useEnvironment();
  const { pathname } = useLocation();
  const isActive = useMemo(
    () => matchMenuItem(pathname, menuItem),
    [pathname, menuItem]
  );
  if ("path" in menuItem) {
    const handleClick = useLinkClickHandler(menuItem.path);
    return (
      <MenuItem label={t(menuItem.label)} to={menuItem.path} active={isActive}
                onClick={handleClick}
                icon={<FontAwesomeIcon icon={menuItem.icon} />}
                subItem={menuItem.subItem ?? false} />
    );
  }

  return (
    <MenuItem
      label={t(menuItem.label)} active={isActive} icon={<FontAwesomeIcon icon={menuItem.icon} />} dropdown
      handleClick={(event: ReactMouseEvent) => event}
    >
      {menuItem.children
        .filter((menuItem: MenuItem) =>
          menuItem.isVisible ? features[menuItem.isVisible as keyof Feature] : true
        )
        .map((child: MenuItem) => (
          <NavMenuItem key={child.label as string} menuItem={child} />
        ))}
    </MenuItem>
  );
}

function matchMenuItem(currentPath: string, menuItem: MenuItem): boolean {

  if ("path" in menuItem) {
    return !!matchPath(menuItem.path, currentPath);
  }
  return menuItem.children.some((child: MenuItem) => matchMenuItem(currentPath, child));
}

type NavLinkProps = {
  to: To;
  isActive: boolean;
};

export const NavLink = ({
                          to,
                          isActive,
                          children
                        }: PropsWithChildren<NavLinkProps>) => {
  const href = useHref(to);
  const handleClick = useLinkClickHandler(to);

  return (
    <NavItem
      data-testid={to}
      to={href}
      isActive={isActive}
      onClick={(event) =>
        // PatternFly does not have the correct type for this event, so we need to cast it.
        handleClick(event as unknown as ReactMouseEvent<HTMLAnchorElement>)
      }
    >
      {children}
    </NavItem>
  );
};
